import { type JSX, lazy } from 'solid-js';
import { Navigate, type RouteDefinition } from '@solidjs/router';
import { AppShell } from '~/widgets/shell/AppShell';
import { AuthLayout } from '~/widgets/shell/AuthLayout';
import { queryKeys } from '~/shared/api/query-keys';
import { RequireAuth, RequirePermission } from '~/shared/api/require-auth';
import { useAuth } from '~/shared/api/auth-context';
import type { PageInfo } from '~/app/page-main-query';

const LoginPage = lazy(() => import('~/pages/auth/LoginPage'));
const TenantSwitchPage = lazy(() => import('~/pages/auth/TenantSwitchPage'));
const SetPasswordPage = lazy(() => import('~/pages/auth/SetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('~/pages/auth/ForgotPasswordPage'));

const DashboardPage = lazy(() => import('~/pages/dashboard/DashboardPage'));
const InvitePage = lazy(() => import('~/pages/invite/InvitePage'));
const SchedulePage = lazy(() => import('~/pages/schedule/SchedulePage'));
const ClientListPage = lazy(() => import('~/pages/clients/ClientListPage'));
const ReportQueuePage = lazy(() => import('~/pages/moderation/ReportQueuePage'));
const ClientProfilePage = lazy(() => import('~/pages/clients/ClientProfilePage'));
const ClientBlacklistPage = lazy(() => import('~/pages/clients/ClientBlacklistPage'));
const ClientBroadcastPage = lazy(() => import('~/pages/clients/ClientBroadcastPage'));
const GuestsPage = lazy(() => import('~/pages/guests/GuestsPage'));
const GuestProfilePage = lazy(() => import('~/pages/guests/GuestProfilePage'));
const BookingListPage = lazy(() => import('~/pages/bookings/BookingListPage'));
const TrainerListPage = lazy(() => import('~/pages/trainers/TrainerListPage'));
const StaffPage = lazy(() => import('~/pages/staff/StaffPage'));
const TrainerSchedulePage = lazy(() => import('~/pages/trainers/TrainerSchedulePage'));
const TrainerChatsPage = lazy(() => import('~/pages/trainers/TrainerChatsPage'));
const TournamentListPage = lazy(() => import('~/pages/tournaments/TournamentListPage'));
const TournamentDetailPage = lazy(() => import('~/pages/tournaments/TournamentDetailPage'));
const NewsListPage = lazy(() => import('~/pages/news/NewsListPage'));
const NewsEditorPage = lazy(() => import('~/pages/news/NewsEditorPage'));
const MatchmakingListPage = lazy(() => import('~/pages/matchmaking/MatchmakingListPage'));
const GroupSessionsPage = lazy(() => import('~/pages/group-sessions/GroupSessionsPage'));
const FinancePage = lazy(() => import('~/pages/finance/FinancePage'));
const PosPage = lazy(() => import('~/pages/finance/PosPage'));
const LoyaltyPage = lazy(() => import('~/pages/loyalty/LoyaltyPage'));
const OrganizationListPage = lazy(() => import('~/pages/organizations/OrganizationListPage'));
const OrganizationDetailPage = lazy(() => import('~/pages/organizations/OrganizationDetailPage'));
const FitnessLayout = lazy(() => import('~/pages/fitness/FitnessLayout'));
const FitnessOverviewPage = lazy(() => import('~/pages/fitness/sections/FitnessOverviewPage'));
const FitnessSchedulePage = lazy(() => import('~/pages/fitness/sections/FitnessSchedulePage'));
const FitnessAttendancePage = lazy(() => import('~/pages/fitness/sections/FitnessAttendancePage'));
const FitnessPassesPage = lazy(() => import('~/pages/fitness/sections/FitnessPassesPage'));
const FitnessAnalyticsPage = lazy(() => import('~/pages/fitness/sections/FitnessAnalyticsPage'));
const FitnessTrainersPage = lazy(() => import('~/pages/fitness/sections/FitnessTrainersPage'));
const FitnessZonesPage = lazy(() => import('~/pages/fitness/sections/FitnessZonesPage'));
const FitnessClassesPage = lazy(() => import('~/pages/fitness/sections/FitnessClassesPage'));
const FitnessSectionNotFound = lazy(
  () => import('~/pages/fitness/sections/FitnessSectionNotFound'),
);
const AccessPage = lazy(() => import('~/pages/access/AccessPage'));
const AnalyticsPage = lazy(() => import('~/pages/analytics/AnalyticsPage'));
const ActivityLogPage = lazy(() => import('~/pages/activity/ActivityLogPage'));
const SettingsPage = lazy(() => import('~/pages/settings/SettingsPage'));
const SettingsClubPage = lazy(() => import('~/pages/settings/SettingsClubPage'));
const SettingsOperationsPage = lazy(() => import('~/pages/settings/SettingsOperationsPage'));
const SettingsPricingLoyaltyPage = lazy(() => import('~/pages/settings/SettingsPricingLoyaltyPage'));
const SettingsCommunicationsPage = lazy(() => import('~/pages/settings/SettingsCommunicationsPage'));
const SettingsIntegrationsPage = lazy(() => import('~/pages/settings/SettingsIntegrationsPage'));
const SettingsPaymentProvidersPage = lazy(() => import('~/pages/settings/SettingsPaymentProvidersPage'));
const SettingsAccessSecurityPage = lazy(() => import('~/pages/settings/SettingsAccessSecurityPage'));
const SettingsFitnessPage = lazy(() => import('~/pages/settings/SettingsFitnessPage'));
const SettingsSystemPage = lazy(() => import('~/pages/settings/SettingsSystemPage'));

const SubscriptionPage = lazy(() => import('~/pages/billing/SubscriptionPage'));
const PlatformDashboardPage = lazy(() => import('~/pages/platform/PlatformDashboardPage'));
const TenantListPage = lazy(() => import('~/pages/platform/TenantListPage'));
const TenantDetailPage = lazy(() => import('~/pages/platform/TenantDetailPage'));
const PlatformPlansPage = lazy(() => import('~/pages/platform/PlatformPlansPage'));
const PlatformPaymentsPage = lazy(() => import('~/pages/platform/PlatformPaymentsPage'));
const PlatformRequisitesPage = lazy(() => import('~/pages/platform/PlatformRequisitesPage'));

const RootIndex = () => {
  const { isAuthenticated } = useAuth();
  return <Navigate href={isAuthenticated() ? '/dashboard' : '/login'} />;
};

const ProtectedShell = (props: { children?: JSX.Element }) => (
  <RequireAuth>
    <AppShell>{props.children}</AppShell>
  </RequireAuth>
);

const PlatformGate = RequirePermission('tenant:manage');
const PlatformConsoleGate = (props: { children?: JSX.Element }) => (
  <PlatformGate>{props.children}</PlatformGate>
);

const page = (mainQuery: readonly unknown[]): PageInfo => ({ mainQuery });

export const routes: RouteDefinition[] = [
  { path: '/', component: RootIndex },
  { path: '/set-password', component: SetPasswordPage },
  {
    path: '/login',
    component: AuthLayout,
    children: [
      { path: '/', component: LoginPage },
      { path: '/tenant', component: TenantSwitchPage },
      { path: '/forgot', component: ForgotPasswordPage },
    ],
  },
  {
    path: '/',
    component: ProtectedShell,
    children: [
      { path: '/dashboard', component: DashboardPage, info: page(['dashboard', 'layout']) },
      { path: '/schedule', component: SchedulePage, info: page(['schedule']) },
      { path: '/clients', component: ClientListPage, info: page(['clients']) },
      { path: '/clients/blacklist', component: ClientBlacklistPage, info: page(['clients', 'blacklist']) },
      { path: '/clients/broadcast', component: ClientBroadcastPage },
      { path: '/clients/:id', component: ClientProfilePage, info: page(['client']) },
      { path: '/moderation/reports', component: ReportQueuePage, info: page(['moderation', 'reports']) },
      { path: '/guests', component: GuestsPage, info: page(['guests']) },
      { path: '/invite', component: InvitePage },
      { path: '/guests/:id', component: GuestProfilePage, info: page(['guest']) },
      { path: '/bookings', component: BookingListPage, info: page(['bookings']) },
      { path: '/trainers', component: TrainerListPage, info: page(['trainers-floor']) },
      { path: '/staff', component: StaffPage, info: page(['club-staff']) },
      { path: '/trainers/chats', component: TrainerChatsPage, info: page(['chat', 'oversight']) },
      { path: '/trainers/:id/schedule', component: TrainerSchedulePage, info: page(['trainer']) },
      { path: '/tournaments', component: TournamentListPage, info: page(['tournaments', 'list']) },
      { path: '/tournaments/:id', component: TournamentDetailPage },
      { path: '/news', component: NewsListPage, info: page(['news', 'list']) },
      { path: '/news/:id', component: NewsEditorPage, info: page(['news', 'detail']) },
      { path: '/matchmaking', component: MatchmakingListPage, info: page(['matchmaking', 'admin']) },
      { path: '/group-sessions', component: GroupSessionsPage, info: page(['group-sessions', 'list']) },
      { path: '/finance', component: FinancePage, info: page(['finance', 'pnl']) },
      { path: '/pos', component: PosPage, info: page(['pos', 'products']) },
      { path: '/loyalty', component: LoyaltyPage, info: page(['loyalty', 'overview']) },
      { path: '/organizations', component: OrganizationListPage, info: page(['organizations', 'list']) },
      { path: '/organizations/:id', component: OrganizationDetailPage, info: page(['organizations', 'detail']) },
      {
        path: '/fitness',
        component: FitnessLayout,
        children: [
          { path: '/', component: FitnessOverviewPage },
          { path: '/schedule', component: FitnessSchedulePage, info: page(queryKeys.groupSessions.fitnessAll()) },
          { path: '/attendance', component: FitnessAttendancePage, info: page(['access', 'fitness', 'visits']) },
          { path: '/passes', component: FitnessPassesPage, info: page(['subscriptions', 'registry']) },
          { path: '/trainers', component: FitnessTrainersPage },
          { path: '/zones', component: FitnessZonesPage, info: page(queryKeys.accessZones.all()) },
          { path: '/analytics', component: FitnessAnalyticsPage, info: page(['analytics', 'attendance']) },
          { path: '/classes', component: FitnessClassesPage, info: page(['fitness-classes', 'catalogue']) },
          { path: '/settings', component: () => <Navigate href="/settings/fitness" /> },
          { path: '*', component: FitnessSectionNotFound },
        ],
      },
      { path: '/access', component: AccessPage },
      { path: '/analytics', component: AnalyticsPage, info: page(['analytics', 'revenue']) },
      { path: '/activity', component: ActivityLogPage, info: page(['activity']) },
      { path: '/settings', component: SettingsPage },
      { path: '/settings/club/:section?', component: SettingsClubPage },
      { path: '/settings/operations/:section?', component: SettingsOperationsPage },
      { path: '/settings/pricing-loyalty/:section?', component: SettingsPricingLoyaltyPage },
      { path: '/settings/communications/:section?', component: SettingsCommunicationsPage },
      { path: '/settings/integrations/:section?', component: SettingsIntegrationsPage },
      { path: '/settings/payment-providers', component: SettingsPaymentProvidersPage, info: page(['settings', 'payment-providers']) },
      { path: '/settings/access-security/:section?', component: SettingsAccessSecurityPage },
      { path: '/settings/fitness/:section?', component: SettingsFitnessPage },
      { path: '/settings/system/:section?', component: SettingsSystemPage },
      { path: '/subscription', component: SubscriptionPage, info: page(['billing', 'subscription']) },
      {
        path: '/platform',
        component: PlatformConsoleGate,
        children: [
          { path: '/', component: PlatformDashboardPage, info: page(['platform', 'dashboard']) },
          { path: '/tenants', component: TenantListPage, info: page(['platform', 'tenants', 'list']) },
          { path: '/tenants/:id', component: TenantDetailPage, info: page(queryKeys.platformTenants.details()) },
          { path: '/plans', component: PlatformPlansPage, info: page(['platform', 'plans']) },
          { path: '/payments', component: PlatformPaymentsPage, info: page(['platform', 'billing', 'awaiting']) },
          { path: '/settings', component: PlatformRequisitesPage, info: page(['platform', 'requisites']) },
        ],
      },
    ],
  },
  { path: '*', component: () => <Navigate href="/" /> },
];
